import { useCallback, useEffect, useRef, useState, type ComponentProps, type ReactElement, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { PdfView } from '../components/PdfView'
import { FieldBox } from '../components/FieldBox'
import { Fingerprint } from '../components/Fingerprint'
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad'
import {
  ApiError,
  consent as sendConsent,
  declineEnvelope,
  endSigningSession,
  getInvitation,
  openSigningSession,
  recordViewed,
  requestChallenge,
  signEnvelope,
  signerDocumentUrl,
  signerCertificateUrl,
  type Challenge,
  type Invitation,
  type PartyMember,
  type SignDocumentView,
  type SignerCorroboration,
  type SignFieldView,
  type SignResult,
  type SignSession,
  type WaitingFor,
} from '../api'
import { count, dateTime, shortHash } from '../format'
import { signerFieldPrompt } from '../fields'
import { fieldIsVisible } from '../visibility'
import { afterRefusal } from '../signRefusals'
import { CERTIFICATE_LATER } from '../certificateWords'

/** Joins names the way a sentence would: "a", "a and b", "a, b and c". */
function names(list: string[]): string {
  if (list.length === 0) return 'somebody else'
  if (list.length === 1) return list[0]
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}

/**
 * Joins names the way a sentence offers a choice: "a", "a or b", "a, b or c".
 * A signing group's members, any one of whom may sign for it.
 */
function orList(list: string[]): string {
  if (list.length <= 1) return list[0] ?? 'somebody'
  return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`
}

/**
 * Party shows everyone on the envelope and where each of them has got to.
 *
 * A signer who is waiting, or who has signed into an unfinished envelope, has
 * no other way to tell whether anything is happening. Somebody the sender's
 * routing took off the signing path is "skipped": the list used to call them
 * waiting, for a turn that never comes.
 *
 * The list has no roles, so it holds people who only open the document or
 * get a copy as well as signers. It was headed "Signers" or "Signing in
 * order", which named a viewer still to open it as a signer still waited for
 * (D12); it is headed as what it is. Somebody who has opened the document reads "opened".
 */
function Party({ party }: { party: PartyMember[] }) {
  if (party.length < 2) return null
  const ordered = party.some((p) => p.routing_order > 1)
  return (
    <div className="card card-tight">
      <div className="small muted" style={{ marginBottom: '.6rem' }}>
        {ordered ? 'On this document, in order' : 'On this document'}
      </div>
      <ol className="party">
        {party.map((p, i) => (
          <li key={`${p.name}-${i}`} className={p.status === 'signed' ? 'is-signed' : undefined}>
            <span className="party-mark" aria-hidden>{p.status === 'signed' ? '✓' : ordered ? p.routing_order : '·'}</span>
            <span className="party-name">
              {p.name}{p.you && <span className="faint"> (you)</span>}
            </span>
            <span className="small faint">{partyState(p)}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * Where one party has got to, in the app's words (mobile/src/components/lists.tsx
 * partyState). A party who is only to open the document, and has not yet,
 * reads "to open": after a signer signed, "waiting" beside a viewer read as
 * if the document still waited on a signature. A copy recipient (`to`
 * "copy") reads "gets a copy": nobody waits for one, and "waiting" beside
 * them read the same way, even after the document was finished (review 4,
 * web-6). A server without `to` keeps "waiting". A member of a signing group
 * another member signed for reads "not needed": the group's signature is in,
 * so nothing waits for them, and "waiting" (or "opened") beside them read as
 * if it still did (review 4, app request 4).
 */
export function partyState(p: PartyMember): 'signed' | 'declined' | 'not needed' | 'skipped' | 'opened' | 'to open' | 'gets a copy' | 'waiting' {
  if (p.status === 'signed') return 'signed'
  if (p.status === 'declined') return 'declined'
  if (p.filled_by_group) return 'not needed'
  if (p.skipped) return 'skipped'
  if (p.status === 'viewed') return 'opened'
  if (p.to === 'view') return 'to open'
  if (p.to === 'copy') return 'gets a copy'
  return 'waiting'
}

/**
 * The facts of the seal on the finished card. The sign answer's fingerprint
 * and size are of the first document this person sealed (internal/api/sign.go
 * passes[0]), which need not be the envelope's first. Unnamed, under a
 * sentence that names the envelope and beside one download per document, they
 * read as the envelope's, and checking another download against them fails.
 * So they are shown only when the envelope is known to hold one document, as
 * the envelope's Technical details and the app do (review 4, app request 1).
 * `documentCount` is 0 when the page holds no session and cannot know.
 */
export function SealedFacts({ done, documentCount }: { done: SignResult; documentCount: number }) {
  const oneDocument = documentCount === 1
  if (!oneDocument && !done.retention_expires_at) return null
  return (
    <table className="table sealed-facts">
      <tbody>
        {oneDocument && (
          <>
            <tr><td className="muted">Document fingerprint</td><td className="mono"><Fingerprint hash={shortHash(done.signed_sha256, 16)} /></td></tr>
            <tr><td className="muted">Size</td><td>{count(done.signed_bytes)} bytes</td></tr>
          </>
        )}
        {done.retention_expires_at && (
          <tr><td className="muted">Kept until</td><td>{dateTime(done.retention_expires_at)}</td></tr>
        )}
      </tbody>
    </table>
  )
}

/** What a field is called when it has to be named in a sentence. */
function fieldTitle(f: SignFieldView): string {
  const prompt = signerFieldPrompt(f.label ?? '', f.kind)
  switch (f.kind) {
    case 'initials': return 'your initials'
    case 'date_signed': return 'the date'
    case 'checkbox':
    case 'radio':
    case 'dropdown':
      return prompt.toLowerCase()
    case 'signature': return 'your signature'
    default: return prompt.toLowerCase()
  }
}

/** The caption on a box drawn over the document — the prompt, never the typed value. */
function boxLabel(f: SignFieldView, value: string | undefined): string {
  if (f.kind === 'signature') return 'Sign here'
  if (f.kind === 'date_signed') return value || 'Date'
  if (f.kind === 'initials') return 'Initials'
  if (f.kind === 'radio' || f.kind === 'dropdown') {
    return value || signerFieldPrompt(f.label ?? '', f.kind)
  }
  return signerFieldPrompt(f.label ?? '', f.kind)
}

/** Whether a required field has actually been answered. */
function isAnswered(f: SignFieldView, value: string | undefined): boolean {
  if (f.kind === 'checkbox') return value === 'true'
  return Boolean((value ?? '').trim())
}

/**
 * FieldInput is one thing the signer has to fill in.
 *
 * A field the signer cannot change is shown rather than hidden, because they
 * are about to sign a document carrying it. Each row can jump the page view to
 * where its box sits, so the signer can see what they are filling in rather
 * than answering a form detached from the document.
 */
function FieldInput({
  field, value, onChange, onGoToPage, onThisPage, where,
}: {
  field: SignFieldView
  value: string
  onChange: (next: string) => void
  onGoToPage: () => void
  onThisPage: boolean
  /**
   * Where the box is, in words. With one document that is a page number; with
   * several it has to name the document too, or "page 2" means two different
   * places in the same list.
   */
  where: string
}) {
  const id = `field-${field.id}`

  if (field.kind === 'checkbox') {
    return (
      <div className="fill-row">
        <label className="check" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          />
          {signerFieldPrompt(field.label ?? '', field.kind)}
          {field.required && <span className="req" aria-label="required">*</span>}
        </label>
        {!onThisPage && <button className="btn-link small" onClick={onGoToPage}>{where}</button>}
      </div>
    )
  }

  if (field.kind === 'radio' && field.options?.length) {
    return (
      <div className="fill-row">
        <div className="row-between">
          <span>
            {field.label || 'Choose one'}
            {field.required && <span className="req" aria-label="required">*</span>}
          </span>
          {!onThisPage && <button className="btn-link small" onClick={onGoToPage}>{where}</button>}
        </div>
        <fieldset className="radio-group">
          <legend className="sr-only">{field.label || 'Choose one'}</legend>
          {field.options.map((opt) => (
            <label key={opt} className="check">
              <input
                type="radio"
                name={id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
              />
              {opt}
            </label>
          ))}
        </fieldset>
      </div>
    )
  }

  if (field.kind === 'dropdown' && field.options?.length) {
    return (
      <div className="fill-row">
        <div className="row-between">
          <label htmlFor={id} style={{ marginBottom: 0 }}>
            {field.label || 'Select one'}
            {field.required && <span className="req" aria-label="required">*</span>}
          </label>
          {!onThisPage && <button className="btn-link small" onClick={onGoToPage}>{where}</button>}
        </div>
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <div className="fill-row">
      <div className="row-between">
        <label htmlFor={id} style={{ marginBottom: 0 }}>
          {field.kind === 'initials' ? 'Your initials' : field.kind === 'date_signed' ? 'Date signed' : signerFieldPrompt(field.label ?? '', field.kind)}
          {field.required && field.editable && <span className="req" aria-label="required">*</span>}
        </label>
        {!onThisPage && <button className="btn-link small" onClick={onGoToPage}>{where}</button>}
      </div>
      <input
        id={id}
        type="text"
        value={value}
        readOnly={!field.editable}
        onChange={(e) => onChange(e.target.value)}
      />
      {!field.editable && (
        <p className="small faint" style={{ margin: '.25rem 0 0' }}>
          Filled in for you when you sign, so it matches the audit trail.
        </p>
      )}
    </div>
  )
}

/**
 * Sign is the signer's entire experience of docsdone.
 *
 * They arrive holding a link and nothing else — no account, no password. The
 * link on its own is an invitation: it says who asked for what, and buys the
 * right to ask for a signing session, which is what everything after it runs
 * on. That is why the document is not here until they open it, and why simply
 * fetching the link leaves no trace: a mail scanner opening it is not a person
 * reading a document.
 *
 * The ceremony is otherwise unchanged and deliberately linear: read the
 * document, agree to sign electronically, draw a signature, done. Consent is a
 * separate, recorded step because ESIGN treats it as a precondition rather than
 * something implied by the act of signing.
 */
/**
 * How this session was opened, in the signer's own terms, so the consent card
 * says what is being recorded about them rather than leaving them to guess.
 *
 * A code shows somebody could read the mailbox. A link the sender was handed
 * shows nothing about the mailbox at all, and saying so plainly is the honest
 * version of the same sentence.
 */
function howAuthenticated(session: SignSession): string {
  const auth = session.you.authentication
  if (auth?.method === 'email_otp') {
    return 'authenticated by a one-time code sent to that address'
  }
  if (auth?.delivery === 'emailed_by_docsdone') {
    return 'using the signing link docsdone emailed to that address'
  }
  return 'using the link the sender gave you'
}

/**
 * The second proof, if there is one: how the signer is also signed in to
 * docsdone. One sentence, beside the first and never folded into it. Being
 * signed in does not change how this session was opened, and a sentence that
 * let it would quietly upgrade the weaker path.
 *
 * Worded from the server's corroboration and nothing else. It used to say
 * "with a Google account" for every corroborated signer, which was false for
 * everybody signed in with a code or through their company. A provider is
 * named only where the server named one, from the mapping the certificate's
 * own sentence uses, so the card and the certificate cannot name two
 * providers for one act; where it named nobody, nobody is named. A direct
 * sign-in's provider reported the address verified, so the sentence speaks of
 * their account there; a brokered one is the organization's own identity
 * provider, which asserted the address, so it says whose sign-in it was and
 * claims no account. These are the app's sentences, word for word.
 */
export function AlsoSignedIn({ corroboration: c }: { corroboration?: SignerCorroboration }) {
  if (!c) return null
  let sentence = 'You are also signed in to docsdone with an account for that address.'
  if (c.method === 'email_otp') {
    sentence = 'You are also signed in to docsdone with a one-time code sent to that address.'
  } else if (c.method === 'federated_oidc' && c.brokered) {
    sentence = c.provider
      ? `You are also signed in to docsdone through ${c.provider}, the sign-in your organization set up.`
      : "You are also signed in to docsdone through your organization's sign-in."
  } else if (c.method === 'federated_oidc' && c.provider) {
    sentence = `You are also signed in to docsdone with your ${c.provider} account for that address.`
  }
  return <>{' '}{sentence}</>
}

/**
 * Why the link could not be read, told apart.
 *
 * Only the server's two answers about the link itself mean it does not work:
 * it has expired, or it is not a link the server knows (a withdrawn one
 * answers the same, on purpose). Everything else used to read "This link
 * doesn't work" too, so a signer with no signal, or meeting a server that had
 * failed, was told to ask the sender for a fresh link they did not need. A
 * server that answered with a sentence of its own is shown with that
 * sentence; no answer at all, or one that is not the API's, is the app's
 * sentence for an unreachable server. Both can be asked again.
 */
export function LinkProblem({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const refused = error instanceof ApiError && error.code !== 'unknown' ? error : null
  if (refused && (refused.code === 'expired_link' || refused.code === 'invalid_link')) {
    return (
      <main className="page page-narrow">
        <div className="card empty">
          <h2>This link doesn't work</h2>
          <p className="muted">{refused.message}</p>
          <p className="small faint">Signing links expire. Ask whoever sent it for a fresh one.</p>
        </div>
      </main>
    )
  }
  return (
    <main className="page page-narrow">
      <div id="link-unreachable" className="card empty">
        <h2>{refused ? 'docsdone could not answer' : 'Could not reach docsdone'}</h2>
        <p className="muted">
          {refused ? refused.message : 'Check your connection and try again.'}
        </p>
        <button id="try-again" className="btn" onClick={onRetry}>Try again</button>
      </div>
    </main>
  )
}

/**
 * How the signer opens a session on their link: one button, or first the code
 * the sender asked docsdone to email them. A requirement with no method is one
 * this version cannot perform, and the exchange refuses it rather than opening
 * a session that claims something that did not happen, so the page says so
 * and offers nothing.
 *
 * Before a code is sent the page says where it will go and nothing about how
 * long it lasts. It used to say "lasts ten minutes", a lifetime the server
 * sets and does not send until the code exists; once it does, the page says
 * when the code stops working, from the server's own answer.
 */
export function Opening({
  sender, stepUp, challenge, attemptsLeft, code, onCode, sending, opening, label, onSend, onOpen,
}: {
  sender: string
  stepUp: Invitation['step_up']
  challenge: Challenge | null
  attemptsLeft: number | null
  code: string
  onCode: (code: string) => void
  sending: boolean
  opening: boolean
  /** What the button that opens the session says. */
  label: string
  onSend: () => void
  onOpen: (step?: { challenge_id: string; code: string }) => void
}) {
  if (stepUp.required && stepUp.method === null) {
    return (
      <p className="small muted">
        {sender} asked for a way of confirming your identity that docsdone cannot do yet. Ask
        them to send the document again without it.
      </p>
    )
  }
  if (stepUp.required && stepUp.method === 'email_otp') {
    if (!challenge) {
      return (
        <>
          <p className="small muted">
            {sender} asked docsdone to email you a code before you open this document. It goes
            to {stepUp.hint || 'the address above'}.
          </p>
          <button className="btn btn-lg" disabled={sending} onClick={onSend}>
            {sending ? <><span className="spinner" /> Sending…</> : 'Send me a code'}
          </button>
        </>
      )
    }
    return (
      <>
        <p className="small muted">
          A six-digit code is on its way to {challenge.sent_to_hint}. It stops working at{' '}
          {dateTime(challenge.expires_at)}.
          {attemptsLeft !== null && attemptsLeft > 0 &&
            ` You have ${attemptsLeft} ${attemptsLeft === 1 ? 'try' : 'tries'} left.`}
        </p>
        <div className="stack-tight">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => onCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-label="The six-digit code"
          />
          <button
            className="btn btn-lg"
            disabled={opening || code.length !== 6}
            onClick={() => onOpen({ challenge_id: challenge.challenge_id, code })}
          >
            {opening ? <><span className="spinner" /> Opening…</> : label}
          </button>
          <button className="btn btn-ghost" disabled={sending} onClick={onSend}>
            Send another code
          </button>
        </div>
      </>
    )
  }
  return (
    <button className="btn btn-lg" disabled={opening} onClick={() => onOpen()}>
      {opening ? <><span className="spinner" /> Opening…</> : label}
    </button>
  )
}

/** What Opening needs from the page, apart from the words on its button. */
type OpenerProps = Omit<ComponentProps<typeof Opening>, 'label'>

/**
 * The signed documents and the certificate, once the document is finished.
 *
 * Each download is a request on a signing session, and a page that was
 * reloaded may hold none: the session lapsed, or it ended, or this is
 * another computer. That page offered one link, "Download your signed copy",
 * to the first document only, and it answered 401 like the certificate's
 * beside it, so a signer who came back for their copies could get none of
 * them, and never the schedules. Without a session the page now offers to
 * open one, which is the signer's own act, with the code first when the
 * sender asked for one, exactly as the invitation does; then every document.
 * "Get your signed copies" is the app's button for the same thing.
 */
export function YourCopies({
  token, documents, certificateReady, error, opener,
}: {
  token: string
  /** Every document of the envelope, from the session this page opened; null when it holds none. */
  documents: SignDocumentView[] | null
  certificateReady: boolean
  error: string
  opener: OpenerProps
}) {
  if (!documents) {
    return (
      <div style={{ marginTop: '1rem' }}>
        {error && <div className="notice notice-bad" style={{ marginBottom: '1rem' }}>{error}</div>}
        <Opening {...opener} label="Get your signed copies" />
      </div>
    )
  }
  return (
    <div className="row" style={{ marginTop: '1rem' }}>
      {documents.length > 1 ? (
        // One link per document. A single "download your signed copy" would
        // hand back the contract and quietly leave the schedules, which is the
        // half a signer is most likely to be asked for.
        documents.map((d) => (
          <a key={d.index} className="btn" href={signerDocumentUrl(token, d.index)} download>
            Download {d.name}
          </a>
        ))
      ) : (
        <a className="btn" href={signerDocumentUrl(token)} download>
          Download your signed copy
        </a>
      )}
      {certificateReady ? (
        <a className="btn btn-secondary" href={signerCertificateUrl(token)}>
          Certificate of completion
        </a>
      ) : (
        <span className="small faint">
          {CERTIFICATE_LATER}
        </span>
      )}
    </div>
  )
}

/**
 * "Done": ends the signing session this browser holds on the link, where the
 * person has finished with it. The page never ended one, so on a shared
 * computer whoever sat down next had the signer's session, and with it the
 * document and every copy, until it lapsed. The app has the same button.
 */
export function DoneHere({ ending, error, onDone }: { ending: boolean; error: string; onDone: () => void }) {
  // A block, not a row: it sits in left-aligned cards and in the centred
  // card after a decline, and follows the text alignment of either.
  return (
    <div style={{ marginTop: '1.25rem' }}>
      {error && <div className="notice notice-bad" style={{ marginBottom: '.75rem' }}>{error}</div>}
      <button id="sign-done" className="btn btn-secondary" disabled={ending} onClick={onDone}>
        {ending ? <><span className="spinner" /> Ending…</> : 'Done'}
      </button>
      <p className="small faint" style={{ margin: '.5rem 0 0' }}>
        On a shared computer, press Done so the document is not left open here.
      </p>
    </div>
  )
}

/**
 * After Done. The session is over and its cookie is gone; the link still
 * works until it expires, and opening it again is a new session, so the page
 * says both and nothing more.
 */
export function Ended({ title, linkExpiresAt }: { title: string; linkExpiresAt: string }) {
  return (
    <main className="page page-narrow">
      <div id="signing-ended" className="card empty">
        <h2>Done</h2>
        <p className="muted">
          &ldquo;{title}&rdquo; is no longer open in this browser. You can close this page.
        </p>
        <p className="small faint">
          Your link still works until {dateTime(linkExpiresAt)}, if you need the document again.
        </p>
      </div>
    </main>
  )
}

/**
 * This person declined, and one refusal closes the document for everybody.
 *
 * The page used to say "The sender has been notified." Nothing is sent to the
 * sender when somebody declines: they see it on the document's page, with the
 * reason, and that is what the page now says. These are the app's words.
 *
 * children is the Done control, when this browser still holds the session
 * the decline was made with.
 */
export function DeclinedByYou({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <main className="page page-narrow">
      <div className="card empty">
        <h2>Declined</h2>
        <p className="muted">
          You declined to sign &ldquo;{title}&rdquo;. The sender can see this on the document&apos;s page.
        </p>
        {children}
      </div>
    </main>
  )
}

/**
 * Somebody else declined, and one refusal closes the document for everybody.
 *
 * The page used to look only at this person's own decline, so anybody else's
 * left them where they were: told to wait for somebody who never would sign,
 * shown "Open document" and the consent card until consent or signing was
 * refused, or, having signed, told the document still needed the others. The
 * invitation says so in envelope.status, which the server answers from the
 * whole roster. Who declined is named from the party list every other state
 * of this page shows, so it tells this person nothing new; the reason stays
 * with the sender. The list itself is not shown: on a closed document it
 * would call everybody else "waiting", and nobody is.
 */
export function DeclinedBySomebodyElse({ title, party, children }: {
  title: string
  party: PartyMember[]
  children?: ReactNode
}) {
  const by = party.filter((p) => p.status === 'declined' && !p.you).map((p) => p.name)
  return (
    <main className="page page-narrow">
      <div className="card empty">
        <h2>This document was declined</h2>
        <p className="muted">
          {by.length ? names(by) : 'Somebody'} declined to sign &ldquo;{title}&rdquo;, so it is closed and
          will not be finished.
        </p>
        <p className="small faint">
          There is nothing for you to do. The sender can see this on the document&apos;s page.
        </p>
        {children}
      </div>
    </main>
  )
}

/**
 * The sender's routing took this person off the signing path, after somebody
 * ahead of them signed. Their turn never opens and the document can finish
 * without them, so "Not your turn yet", which they used to see for ever, was
 * false. The server refuses them in the same words.
 */
export function Skipped({ title, party }: { title: string; party: PartyMember[] }) {
  return (
    <main className="page page-narrow">
      <div className="card empty">
        <h2>No longer needed</h2>
        <p className="muted">
          Your signature is no longer needed on &ldquo;{title}&rdquo;. The sender&apos;s rules for this
          document skipped you.
        </p>
        <p className="small faint">There is nothing for you to do.</p>
      </div>
      <Party party={party} />
    </main>
  )
}

/**
 * Somebody ahead of this person in the routing order still has something to
 * do. Showing the signing form here would let them fill it in and then be
 * refused, so the wait is stated plainly instead.
 *
 * Worded from waiting_for, the server's own answer to whom the turn waits
 * for, in the words the server refuses an early signature with. It used to be
 * read off the party list, which has no roles: a party who only has to open
 * the document was described as one more signer, and a copy recipient, a
 * viewer who had already opened it, a party routing skipped and the rest of a
 * signing group somebody had signed for were all named as people the turn was
 * waiting for. A server older than the field is read the old way.
 */
export function NotYourTurn({ invitation }: { invitation: Invitation }) {
  const title = invitation.envelope.title
  const waiting: WaitingFor[] = invitation.waiting_for ?? invitation.party
    .filter((p) => p.routing_order < invitation.you.routing_order && p.status !== 'signed')
    .map((p) => ({ name: p.name, routing_order: p.routing_order, to: 'sign' }))
  const what = waiting.map((w) =>
    w.members?.length ? `${orList(w.members)} needs to sign it`
      : w.to === 'view' ? `${w.name} needs to open it`
        : `${w.name} needs to sign it`)
  const viewOnly = invitation.you.role === 'needs_to_view' || invitation.you.role === 'cc'
  const once = waiting.length === 1 && waiting[0].members?.length ? 'one of them has' : 'they have'
  return (
    <main className="page page-narrow">
      <div className="card empty">
        <h2>Not your turn yet</h2>
        <p className="muted">
          {what.length
            ? <>Before &ldquo;{title}&rdquo; reaches you, {names(what)}.</>
            : <>&ldquo;{title}&rdquo; goes to somebody else before it reaches you.</>}
          {' '}You will be able to {viewOnly ? 'open it' : 'sign'} once {once}.
        </p>
        <p className="small faint">
          There is nothing to do right now. Your link stays valid until{' '}
          {dateTime(invitation.invitation_expires_at)}.
        </p>
      </div>
      <Party party={invitation.party} />
    </main>
  )
}

/**
 * The states the invitation alone decides, before there is a document to
 * open, in the order they are checked: this person declined; somebody else
 * did; routing skipped this person; it is not their turn; their signing group
 * is already signed for. Each makes everything after it false, so the order
 * is the point. A refusal comes first because it ends the document for
 * everybody: nobody after the person who declined is waiting for them, and
 * nobody who signed is waiting for the rest. A skip comes before the turn,
 * because a skipped person's turn never opens.
 *
 * Null when none of them holds, and the page goes on to the outcome, the
 * invitation and the ceremony. doneHere is the Done control, given only
 * while this browser holds a session. A decline, this person's or anybody
 * else's, is what can leave one open: a decline revokes nothing of the other
 * signers, so a session opened before it still reads the document. A group
 * somebody else signed for cannot, because that revokes the sessions of the
 * rest of the group.
 */
function beforeTheDocument(invitation: Invitation, declinedNow: boolean, doneHere?: ReactNode): ReactElement | null {
  const title = invitation.envelope.title
  if (declinedNow || invitation.declined) return <DeclinedByYou title={title}>{doneHere}</DeclinedByYou>
  if (invitation.envelope.status === 'declined') {
    return <DeclinedBySomebodyElse title={title} party={invitation.party}>{doneHere}</DeclinedBySomebodyElse>
  }
  if (invitation.skipped) return <Skipped title={title} party={invitation.party} />
  if (!invitation.your_turn && !invitation.already_signed) return <NotYourTurn invitation={invitation} />

  if (invitation.already_signed && invitation.group_filled_by) {
    return (
      <main className="page page-narrow">
        <div className="card empty">
          <h2>Already signed</h2>
          <p className="muted">
            {invitation.group_filled_by} signed &ldquo;{title}&rdquo; on behalf of your signing
            group. Your link is no longer needed.
          </p>
        </div>
        <Party party={invitation.party} />
      </main>
    )
  }
  return null
}

/** The same states as a component, so their order can be rendered and read on its own. */
export function BeforeTheDocument({ invitation, declinedNow = false, doneHere }: {
  invitation: Invitation
  declinedNow?: boolean
  doneHere?: ReactNode
}) {
  return beforeTheDocument(invitation, declinedNow, doneHere)
}

/**
 * This person has signed and the document is not finished yet: who it still
 * needs.
 *
 * Just after signing, the server's answer names them, by its own count
 * (waiting_on). After a reload, the invitation's still_to_sign does, by the
 * same count: the party list has no roles, so read alone it named a copy
 * recipient, a viewer who had opened it and the rest of a signing group
 * somebody had signed for as people the document still needed. A group
 * nobody has signed for is named with the members who may sign for it. A
 * server older than still_to_sign is read the old way, with skipped people
 * left out.
 *
 * Once the server has answered the signature, the invitation read before it
 * is never consulted again: its still_to_sign still names this person. An
 * empty waiting_on means no signature is needed, which happens when only a
 * viewer has yet to open the document, and the page then says that rather
 * than naming the signer as somebody it still needs.
 *
 * An answer without waiting_on says nothing about who is left: the server
 * leaves it out when it could not read the document after the signature,
 * as well as (until it always sends the list) when nobody is. Read as
 * "nobody", that told a signer every signature was in while another signer
 * had not signed, so the page then says only that this person has signed.
 *
 * The sentence at the foot holds in every one of these states. It said
 * "Once everyone has signed", which is false when everyone has and a viewer
 * has still to open it: the store finishes a document only once every
 * signature is in and every viewer has opened it.
 *
 * children is the Done control, while this browser holds a session.
 *
 * party is the list as the server gives it once more after the signature
 * (partyAfterSigning). Until it arrives, or if that read fails, the list read
 * before is shown with this person marked signed.
 */
export function SignatureIsIn({ invitation, done, party, children }: {
  invitation: Invitation
  done: SignResult | null
  party?: PartyMember[] | null
  children?: ReactNode
}) {
  // null: the server's answer did not say who is left.
  const waiting: string[] | null = done
    ? done.waiting_on ?? null
    : invitation.still_to_sign?.map((w) => (w.members?.length ? `${w.name} (${orList(w.members)})` : w.name))
      ?? invitation.party.filter((p) => p.status !== 'signed' && !p.skipped).map((p) => p.name)
  return (
    <main className="page page-narrow">
      <div className="card">
        <h1>Your signature is in</h1>
        <p className="muted">
          You have signed &ldquo;{invitation.envelope.title}&rdquo;.
          {waiting === null
            ? null
            : waiting.length > 0
              ? <> It is not finished yet: it still needs {names(waiting)}.</>
              : ' Every signature it needs is in. It is finished once everybody asked to open it has opened it.'}
        </p>
        {done && (
          <div className="notice notice-good" style={{ margin: '1rem 0' }}>
            <strong>Verified.</strong> Your signature was checked immediately after it was
            applied and is intact.
          </div>
        )}
        <p className="small faint" style={{ marginBottom: 0 }}>
          Once it is finished, the signed document and its certificate of completion
          become available here. Your link stays valid until{' '}
          {dateTime(invitation.invitation_expires_at)}.
        </p>
        {children}
      </div>
      <Party party={done ? party ?? signedByYou(invitation.party) : invitation.party} />
    </main>
  )
}

/**
 * The party list once more, after a signature that leaves the document
 * unfinished. The list read before the signature cannot say what it did to
 * the others: when this person signed for a signing group, every other
 * member is no longer waited for (filled_by_group), and the page has no
 * group ids to work that out. So the page showed "Alan Turing waiting" under
 * "it still needs Carol Diaz" until a reload said "not needed" (review 5,
 * web-3). Reading the invitation records nothing, so asking again is safe.
 * A failed read leaves the list as it was: the signature is in either way.
 */
export async function partyAfterSigning(
  token: string,
  done: SignResult,
  read: (token: string) => Promise<Invitation> = getInvitation,
): Promise<PartyMember[] | null> {
  if (done.completed) return null
  try {
    return (await read(token)).party
  } catch {
    return null
  }
}

/**
 * The party list as it stands once the server has answered this person's
 * signature. The list was read before they signed, so it showed them as
 * waiting on the page that says their signature is in (D12).
 */
export function signedByYou(party: PartyMember[]): PartyMember[] {
  return party.map((p) => (p.you ? { ...p, status: 'signed' } : p))
}

/**
 * The party list once the server has recorded that this person opened the
 * document. The list was read before, so their own row said "waiting" beside
 * another signer who read "opened", above the document they were reading.
 */
export function openedByYou(party: PartyMember[]): PartyMember[] {
  return party.map((p) => (p.you && p.status === 'pending' ? { ...p, status: 'viewed' } : p))
}

/**
 * What this person was actually asked to do, in their own terms.
 *
 * Telling a copy recipient they were "asked to sign" is not a wording slip: it
 * is the page telling them they have an obligation they do not have, on a
 * document that records what everybody did. Someone who believes they must sign
 * and finds no way to will conclude the product is broken; someone who signs
 * because they were told to has been misled about their own act.
 */
/**
 * What this visit has managed to put on the screen, which decides what the
 * signer can press. Agreeing and signing are about a document the person has
 * seen, and the view is recorded as the page is drawn, so both wait until it
 * is. pageShown is set when a page is drawn, before the view is recorded, so a
 * view the server does not record does not hold anybody up.
 *
 * cannot holds every document of the envelope that failed to load or draw on
 * this visit, and a later draw of another document never takes one out: one
 * Sign signs every document, so a document that could not be shown holds the
 * whole ceremony, as the app's documentShown does (any document marked
 * 'cannot', and markDrawn never overwrites the mark). It held only the
 * document on screen, and drawing any other page or document cleared it, so
 * a signer who picked the second document in the rail could agree to and
 * sign the first, which the page had just said could not be signed until it
 * was shown (review 6, web-1). A reload starts a new visit, which is the
 * remedy the page offers.
 */
export interface Viewing {
  pageShown: boolean
  cannot: number[]
}

export const NOTHING_SHOWN: Viewing = { pageShown: false, cannot: [] }

/** A page of some document was drawn. Never clears a document that could not be. */
export function drawn(v: Viewing): Viewing {
  return v.pageShown ? v : { ...v, pageShown: true }
}

/** The document at this index could not be loaded or drawn. */
export function couldNotShow(v: Viewing, at: number): Viewing {
  return v.cannot.includes(at) ? v : { ...v, cannot: [...v.cannot, at] }
}

/**
 * Where the visit has got to: 'unreadable' while any document of the
 * envelope could not be shown, 'shown' once a page was drawn, else 'waiting'.
 * A document that cannot be drawn cannot be agreed to or signed here at all:
 * the page offers only what is wrong and Decline (review 5,
 * app-session-ceremony-3, the web's half).
 */
export function documentState(v: Viewing): 'waiting' | 'shown' | 'unreadable' {
  if (v.cannot.length > 0) return 'unreadable'
  return v.pageShown ? 'shown' : 'waiting'
}

/**
 * In place of agreeing and signing, for a document the page cannot draw.
 * unshown names the documents that could not be shown when the envelope holds
 * several: the one on screen may be another, which drew, and one Sign signs
 * them all, so the card says which one holds it up.
 */
export function CannotShow({ unshown }: { unshown?: string[] }) {
  const several = unshown && unshown.length > 0
  return (
    <div className="card" id="sign-cannot-show">
      <h2>
        {several ? `${names(unshown.map((n) => `“${n}”`))} cannot be shown` : 'This document cannot be shown'}
      </h2>
      <div className="notice notice-bad" style={{ marginBottom: '1rem' }}>
        {several
          ? 'Signing once signs every document, so none of them can be signed until it is shown. Reload the page to try again.'
          : 'It cannot be signed until it is shown. Reload the page to try again.'}
      </div>
      <p className="small muted" style={{ marginBottom: 0 }}>
        If it still cannot be shown, you can decline to sign.
      </p>
    </div>
  )
}

/**
 * The same card for a person who only opens the document or gets a copy. The
 * view-only page passed the PDF's failure to the page's error, which that page
 * never draws, so a viewer whose document would not load saw an empty frame
 * and nothing else (R7-web, found, not fixed). A copy recipient can still
 * download their copy from the card below it. Somebody asked to open it cannot
 * open it any other way: the view is recorded only when the page is drawn, so
 * the card says what that means and whom to tell.
 */
export function ViewerCannotShow({ copy, sender, unshown }: { copy: boolean; sender: string; unshown?: string[] }) {
  const several = unshown && unshown.length > 0
  return (
    <div className="card" id="view-cannot-show">
      <h2>
        {several ? `${names(unshown.map((n) => `\u201c${n}\u201d`))} cannot be shown` : 'This document cannot be shown'}
      </h2>
      <div className="notice notice-bad" style={{ marginBottom: '1rem' }}>
        Reload the page to try again.
      </div>
      <p className="small muted" style={{ marginBottom: 0 }}>
        {copy
          ? 'You can still download your copy.'
          : `It counts as opened only once it is shown here. If it still cannot be shown, tell ${sender}: the document cannot be finished until you have opened it.`}
      </p>
    </div>
  )
}

/**
 * Consent is asked for before signing, as its own recorded step, and only once
 * the document is on screen: "I agree" stays off while the page is drawn.
 */
export function ConsentCard({ session, documentCount, ready, onAgree, error = '' }: {
  session: SignSession
  documentCount: number
  ready: boolean
  onAgree: () => void
  /** Why agreeing was refused. The card drew none, so a refused "I agree" seemed to do nothing (review 6, web-2). */
  error?: string
}) {
  const many = documentCount > 1
  return (
    <div className="card">
      <h2>Before you sign</h2>
      <p className="small muted">
        You are acting as {session.you.identifier}, {howAuthenticated(session)}.
        <AlsoSignedIn corroboration={session.you.authentication?.corroboration} />
      </p>
      <p className="small muted">
        {/* How many documents, said before they agree rather than after.
            Consent is what ESIGN turns on, and a person agreeing to
            sign "this document" when there are three has not been told
            what they are agreeing to. */}
        You are about to sign{' '}
        {many ? `these ${documentCount} documents` : 'this document'}
        {' '}electronically. An electronic signature has the same legal effect as signing
        on paper. We will record when you opened{' '}
        {many ? 'them' : 'the document'}, when you agreed to this, when you
        signed, and the IP address you did it from — and include that record with the
        signed {many ? 'documents' : 'document'}.
      </p>
      <p className="small muted">
        You can decline instead, and you can download a copy of the signed{' '}
        {many ? 'documents' : 'document'} afterwards to keep.
      </p>
      {error && <div className="notice notice-bad" role="alert" style={{ marginBottom: '1rem' }}>{error}</div>}
      <button className="btn" id="sign-agree" disabled={!ready} onClick={onAgree}>I agree to sign electronically</button>
    </div>
  )
}

function askedTo(role: string): string {
  switch (role) {
    case 'cc':
      return 'sent you a copy of'
    case 'needs_to_view':
      return 'asked you to open'
    case 'witness':
      return 'asked you to witness'
    default:
      return 'asked you to sign'
  }
}

export function Sign() {
  const { token = '' } = useParams()
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [session, setSession] = useState<SignSession | null>(null)
  // Why the invitation could not be read, and how many times it has been
  // asked for: "Try again" asks once more.
  const [loadError, setLoadError] = useState<unknown>(null)
  const [asked, setAsked] = useState(0)
  // The deployment is not open to the public yet. The link is fine; the door
  // is shut, and the signer is told that rather than that their link is dead.
  const [notOpen, setNotOpen] = useState(false)
  const [opening, setOpening] = useState(false)
  const [page, setPage] = useState(1)
  // An envelope can carry several documents, signed in this one act. This is
  // which one is on screen; it indexes session.documents.
  const [docAt, setDocAt] = useState(0)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const [consented, setConsented] = useState(false)
  const [typedName, setTypedName] = useState('')
  // Non-signature field values, keyed by field id. A checkbox is "true"/"false".
  const [values, setValues] = useState<Record<string, string>>({})
  const [hasInk, setHasInk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<SignResult | null>(null)
  const [declining, setDeclining] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [declined, setDeclined] = useState(false)
  // Why declining was refused, said in the decline card. It used to go to the
  // page's error, which the consent and cannot-show cards never draw, so a
  // refused decline did nothing anybody could see, on the one card where
  // Decline is the only way forward (review 6, web-2).
  const [declineError, setDeclineError] = useState('')
  // The server's sentence when this person can never sign this envelope
  // (afterRefusal 'final'). Sign document stays off once it is set.
  const [unsignable, setUnsignable] = useState('')
  // Done: the session this browser held has been ended, or is being.
  const [ending, setEnding] = useState(false)
  const [endError, setEndError] = useState('')
  const [ended, setEnded] = useState(false)

  // The one-time code, when the sender asked for one. challenge is what
  // docsdone said after it put a code in the mailbox; code is what the person
  // has typed back.
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)

  const pad = useRef<SignaturePadHandle>(null)
  // The view is recorded once per visit, after the document is actually on
  // screen. Paging through it is not a second reading.
  const recorded = useRef(false)
  // Set once the server has recorded the view, so this person's own row in
  // the party list says so without reading the invitation again.
  const [seen, setSeen] = useState(false)
  // Whether a page has been drawn on this visit, and every document that
  // could not be (Viewing).
  const [viewing, setViewing] = useState<Viewing>(NOTHING_SHOWN)
  // The party list as the server gives it after this person's signature.
  const [partyAfter, setPartyAfter] = useState<PartyMember[] | null>(null)
  // A failed exchange must not be retried on every render.
  const tried = useRef(false)

  useEffect(() => {
    getInvitation(token)
      .then((inv) => {
        setInvitation(inv)
        setConsented(inv.consented)
        setTypedName(inv.you.name)
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'not_open') setNotOpen(true)
        else setLoadError(e)
      })
  }, [token, asked])

  const openDocument = useCallback(async (step?: { challenge_id: string; code: string }) => {
    tried.current = true
    setOpening(true)
    setError('')
    try {
      const s = await openSigningSession(token, step)
      setSession(s)
      setDocAt(s.field?.document_index ?? 0)
      setPage(s.field?.page ?? 1)
      setConsented(s.consented)
      // Start from what the server suggests: initials derived from their
      // name, the date as it will be stamped. A checkbox starts unticked.
      const initial: Record<string, string> = {}
      for (const f of s.fields) {
        if (f.kind === 'checkbox') initial[f.id] = 'false'
        else if (f.prefill) initial[f.id] = f.prefill
      }
      setValues(initial)
    } catch (e) {
      // The refusals a code can produce each need their own words. A wrong
      // code is worth saying how many tries are left for; an expired or
      // finished one means asking for a new code, not typing harder.
      if (e instanceof ApiError) {
        switch (e.code) {
          case 'bad_code':
            setAttemptsLeft(e.attemptsLeft ?? null)
            setError(
              e.attemptsLeft && e.attemptsLeft > 0
                ? `That code is not right. ${e.attemptsLeft} ${e.attemptsLeft === 1 ? 'try' : 'tries'} left.`
                : 'That code is not right.',
            )
            break
          case 'challenge_expired':
          case 'too_many_attempts':
            setChallenge(null)
            setCode('')
            setAttemptsLeft(null)
            setError(
              e.code === 'challenge_expired'
                ? 'That code has expired. Ask for a new one.'
                : 'Too many wrong codes were tried, so that one is finished. Ask for a new one.',
            )
            break
          case 'step_up_required':
            // The page asked without a code; the card below now knows to ask.
            setError('')
            break
          case 'envelope_declined':
          case 'group_filled':
            settle(e)
            break
          default:
            setError(e.message)
        }
        return
      }
      setError(e instanceof Error ? e.message : 'Could not open the document.')
    } finally {
      setOpening(false)
    }
  }, [token])

  /** Asks docsdone to email a code, and remembers which challenge it is. */
  const sendCode = useCallback(async () => {
    setSending(true)
    setError('')
    setAttemptsLeft(null)
    try {
      setChallenge(await requestChallenge(token))
      setCode('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send you a code.')
    } finally {
      setSending(false)
    }
  }, [token])

  // A session that is still live means this browser already opened the
  // document, so there is nothing to ask them for a second time.
  //
  // Not where the sender asked for a code. The exchange refuses one made
  // without a code whatever else is true, so reopening the page would answer
  // 428, put the signer back at "Send me a code", and spend one of the three
  // an hour they are allowed. Three reloads and they could not open their own
  // document until the hour turned over.
  useEffect(() => {
    if (invitation?.session.active && !invitation.step_up.required && !tried.current) {
      void openDocument()
    }
  }, [invitation, openDocument])

  /**
   * Called once the PDF is on screen. Seeing it is what is being recorded.
   *
   * Which document is on screen goes with it. The record used to name the
   * envelope's first document whatever was showing, so "Viewed the document at
   * T" could carry the fingerprint of a file they never opened — and that
   * sentence, with that fingerprint, is what the certificate prints.
   */
  function onRendered(next: { width: number; height: number }) {
    setSize(next)
    setViewing(drawn)
    if (recorded.current) return
    recorded.current = true
    void recordViewed(token, docAt).then(() => setSeen(true), () => {
      // Nothing the signer can do about it, and it must not block signing.
    })
  }

  /**
   * A refusal saying the document, the link or the session changed while this
   * person was on it (afterRefusal). For the document or the link, the page
   * reads the invitation again, so it shows the closed card, "Already signed"
   * or "This link doesn't work" instead of the server's sentence over a card
   * that cannot finish. For a session that ended, it goes back to the
   * invitation, which offers to open the document again, with the server's
   * sentence above that button. True when the refusal was settled here.
   */
  function settle(e: unknown): boolean {
    if (!(e instanceof ApiError)) return false
    const step = afterRefusal(e.code)
    if (step === 'say') return false
    setDeclineError('')
    if (step === 'reopen') {
      setSession(null)
      setDeclining(false)
      setError(e.message)
    } else {
      setError('')
    }
    setAsked((n) => n + 1)
    return true
  }

  async function agree() {
    setError('')
    try {
      await sendConsent(token)
      setConsented(true)
    } catch (e) {
      if (settle(e)) return
      setError(e instanceof Error ? e.message : 'Could not record your consent.')
    }
  }

  async function submit() {
    const png = pad.current?.toDataURL()
    if (!png) {
      setError('Draw your signature in the box before signing.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const result = await signEnvelope(token, png, typedName.trim(), values)
      setDone(result)
      void partyAfterSigning(token, result).then(setPartyAfter)
    } catch (e) {
      if (settle(e)) return
      if (e instanceof ApiError && afterRefusal(e.code) === 'final') {
        setUnsignable(e.message)
        return
      }
      setError(e instanceof Error ? e.message : 'Signing failed.')
    } finally {
      setBusy(false)
    }
  }

  async function decline() {
    setBusy(true)
    setDeclineError('')
    try {
      await declineEnvelope(token, declineReason.trim())
      setDeclined(true)
    } catch (e) {
      if (settle(e)) return
      setDeclineError(e instanceof Error ? e.message : 'Could not decline.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Done. A session that had already lapsed is as over as this one is about
   * to be, so the server's "open the document again" answer counts as ended.
   */
  async function finish() {
    setEnding(true)
    setEndError('')
    try {
      await endSigningSession(token)
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'session_required')) {
        setEndError(e instanceof Error ? e.message : 'Could not end the session. Try again.')
        setEnding(false)
        return
      }
    }
    setSession(null)
    setEnding(false)
    setEnded(true)
  }

  // --------------------------------------------------------------- outcomes

  if (notOpen) {
    return (
      <main className="page page-narrow">
        <div id="not-open" className="card empty">
          <h2>Not open yet</h2>
          <p className="muted">docsdone is being set up and is not taking documents yet.</p>
          <p className="small faint">Your link is fine. It will work once we open.</p>
        </div>
      </main>
    )
  }

  if (loadError) {
    return (
      <LinkProblem
        error={loadError}
        onRetry={() => {
          setLoadError(null)
          setAsked((n) => n + 1)
        }}
      />
    )
  }

  if (!invitation) {
    return <main className="page page-narrow"><p className="muted">Loading…</p></main>
  }

  const title = invitation.envelope.title

  // How a session is opened on this link, wherever the page offers to: what
  // the sender asked for beyond the link, and the code if it is a code.
  const opener = {
    sender: invitation.sender.name,
    stepUp: invitation.step_up,
    challenge,
    attemptsLeft,
    code,
    onCode: setCode,
    sending,
    opening,
    onSend: () => void sendCode(),
    onOpen: (step?: { challenge_id: string; code: string }) => void openDocument(step),
  }

  if (ended) return <Ended title={title} linkExpiresAt={invitation.invitation_expires_at} />

  // Done is offered where the person has finished with the link and this
  // browser holds a session on it: one this page opened, or one the
  // invitation says is still live. With neither there is nothing to end.
  const doneHere = session || invitation.session.active
    ? <DoneHere ending={ending} error={endError} onDone={() => void finish()} />
    : null

  const before = beforeTheDocument(invitation, declined, doneHere)
  if (before) return before

  if (done || invitation.already_signed) {
    // "Signed" and "finished" are different things once there is more than one
    // signer, and the difference decides whether there is a document to
    // download yet.
    const finished = done ? done.completed : invitation.envelope.status === 'completed'
    const certificateReady = done ? done.certificate_ready : invitation.certificate_ready

    if (!finished) return <SignatureIsIn invitation={invitation} done={done} party={partyAfter}>{doneHere}</SignatureIsIn>

    return (
      <main className="page page-narrow">
        <div className="card">
          <h1>Signed</h1>
          <p className="muted">
            &ldquo;{title}&rdquo; has been signed and sealed. A copy has been kept for {invitation.sender.name}.
          </p>

          {done && (
            <>
              <div className="notice notice-good" style={{ margin: '1rem 0' }}>
                <strong>Verified.</strong> The sealed document was checked immediately after signing
                and its signature is intact.
              </div>
              <SealedFacts done={done} documentCount={session ? session.documents?.length || 1 : 0} />
            </>
          )}

          <YourCopies
            token={token}
            documents={session ? session.documents ?? [] : null}
            certificateReady={certificateReady}
            error={error}
            opener={opener}
          />
          <p className="small faint" style={{ marginTop: '1rem', marginBottom: 0 }}>
            Keep this file. The signature is embedded inside the PDF, so it can be verified in Adobe
            Acrobat or any compatible reader. It stays valid whether or not docsdone keeps a copy.
          </p>
          {doneHere}
        </div>
      </main>
    )
  }

  // ------------------------------------------------------- the invitation

  // Until they ask for a session there is no document and nothing recorded.
  if (!session) {
    return (
      <main className="page page-narrow">
        <div className="card">
          <h1>{title}</h1>
          <p className="muted">
            {invitation.sender.name} {askedTo(invitation.you.role)} &ldquo;{title}&rdquo; as{' '}
            {invitation.you.name} &lt;{invitation.you.identifier}&gt;.
          </p>
          <p className="small faint">
            Opening the document records that you saw it. This link stays valid until{' '}
            {dateTime(invitation.invitation_expires_at)}.
          </p>

          {error && <div className="notice notice-bad" style={{ marginBottom: '1rem' }}>{error}</div>}

          <Opening {...opener} label="Open document" />
        </div>
        <Party party={invitation.party} />
      </main>
    )
  }

  // --------------------------------------------------------------- ceremony

  const viewOnly = session.you.role === 'needs_to_view' || session.you.role === 'cc'

  // Every document of the envelope, and how to get to any of them. Used by the
  // viewing branch as well as the signing one: a copy recipient of a contract
  // with two schedules was shown the contract and given no way to reach the
  // schedules, and a needs-to-view party discharged the whole envelope's
  // obligation by opening one of three.
  const documents = session.documents?.length
    ? session.documents
    : [{ index: 0, name: title, page_count: session.page_count, pages: session.pages, yours: true }]
  const showing = documents.find((d) => d.index === docAt) ?? documents[0]
  const pageCount = showing.page_count

  /** Show one of the envelope's documents, from a chosen page. */
  const goTo = (at: number, to = 1) => {
    setDocAt(at)
    setPage(to)
  }
  /** Where a box is, said the way a person would say it. */
  const whereIs = (f: { document_index: number; page: number }) =>
    documents.length > 1
      ? `${documents.find((d) => d.index === f.document_index)?.name ?? 'another document'}, page ${f.page}`
      : `page ${f.page}`

  /** The rail that names the documents and says which are yours to sign. */
  const rail = documents.length > 1 && (
    <nav className="doc-tabs" aria-label="Documents in this envelope">
      {documents.map((d) => (
        <button
          key={d.index}
          className={`doc-tab${d.index === docAt ? ' is-current' : ''}`}
          aria-current={d.index === docAt ? 'true' : undefined}
          onClick={() => goTo(d.index)}
        >
          <span className="doc-tab-n">{d.index + 1}</span>
          <span className="doc-tab-name">{d.name}</span>
          <span className="small">{d.yours && !viewOnly ? 'you sign' : `${d.page_count} pp`}</span>
        </button>
      ))}
    </nav>
  )

  if (viewOnly) {
    const copyRole = session.you.role === 'cc'
    return (
      <main className="page">
        <div className="page-head">
          <h1>{title}</h1>
          <p className="muted">
            {copyRole
              ? `${invitation.sender.name} sent you a copy of this document.`
              : `${invitation.sender.name} needs you to open and read this document before it can be finished.`}
            {' '}Link valid until {dateTime(invitation.invitation_expires_at)}.
          </p>
        </div>

        {rail}

        <div className="grid-doc grid-doc-sign">
          <div>
            <PdfView
              src={signerDocumentUrl(token, docAt)}
              page={page}
              onRendered={onRendered}
              onError={() => setViewing((v) => couldNotShow(v, docAt))}
            />
            {pageCount > 1 && (
              <div className="page-nav">
                <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  ← Previous
                </button>
                <span>Page {page} of {pageCount}</span>
                <button
                  className="btn btn-ghost"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>

          <div className="stack">
            {invitation.party.length > 1 && <Party party={seen ? openedByYou(invitation.party) : invitation.party} />}

            {documentState(viewing) === 'unreadable' && (
              <ViewerCannotShow
                copy={copyRole}
                sender={invitation.sender.name}
                unshown={documents.length > 1 ? documents.filter((d) => viewing.cannot.includes(d.index)).map((d) => d.name) : undefined}
              />
            )}

            <div className="card">
              <h2>{copyRole ? 'Your copy' : 'Confirm you saw it'}</h2>
              <p className="small muted">
                {copyRole
                  ? documents.length > 1
                    ? `You were sent ${documents.length} documents. Each is shown on the left; `
                      + 'you can download a copy of each to keep.'
                    : 'The signed document is shown on the left. You can download a copy to keep.'
                  : documents.length > 1
                    ? `There are ${documents.length} documents to read. Opening them records that you `
                      + 'did; the envelope cannot finish until you have.'
                    : 'Reading the document on the left records that you opened it. '
                      + 'The envelope cannot finish until you have.'}
              </p>
              {/* One link per document. A single "the signed PDF" handed back
                  the first and left the schedules, which is the half a copy
                  recipient is most likely to have been sent them for. */}
              {copyRole && documents.map((d) => (
                <a
                  key={d.index}
                  className="btn"
                  href={signerDocumentUrl(token, d.index)}
                  style={{ marginBottom: '.5rem' }}
                  download
                >
                  {documents.length > 1 ? `Download ${d.name}` : 'Download the signed PDF'}
                </a>
              ))}
              {invitation.certificate_ready && (
                <a className="btn btn-secondary" href={signerCertificateUrl(token)} style={{ marginTop: copyRole ? '.75rem' : 0 }}>
                  Certificate of completion
                </a>
              )}
              {doneHere}
            </div>
          </div>
        </div>
      </main>
    )
  }

  // Everything the signer has to deal with besides drawing their signature.
  // The date is included even though they cannot change it: they are signing a
  // document that will carry it, so they should see it.
  const shown = session.fields.filter((f) => fieldIsVisible(session.fields, values, f.id))
  const fillable = shown.filter((f) => f.kind !== 'signature')
  const missing = fillable.filter(
    (f) => f.editable && f.required && !isAnswered(f, values[f.id]),
  )
  const signatureField = session.field
  const shownState = documentState(viewing)

  return (
    <main className="page">
      <div className="page-head">
        <h1>{title}</h1>
        <p>
          {invitation.sender.name} {askedTo(invitation.you.role)}{' '}
          {documents.length > 1 ? `these ${documents.length} documents` : 'this document'}.
          {' '}Link valid until {dateTime(invitation.invitation_expires_at)}.
        </p>
      </div>

      {rail}

      <div className="grid-doc grid-doc-sign">
        <div>
          <PdfView
            src={signerDocumentUrl(token, docAt)}
            page={page}
            onRendered={onRendered}
            onError={() => setViewing((v) => couldNotShow(v, docAt))}
          >
            {shown
              .filter((f) => f.page === page && f.document_index === docAt)
              .map((f) => (
                <FieldBox
                  key={f.id}
                  rect={f.rect}
                  pageWidth={size.width}
                  pageHeight={size.height}
                  onChange={() => {}}
                  label={boxLabel(f, values[f.id])}
                  readOnly
                />
              ))}
          </PdfView>

          {pageCount > 1 && (
            <div className="page-nav">
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ← Previous
              </button>
              <span>
                Page {page} of {pageCount}
                {signatureField
                  && (page !== signatureField.page || docAt !== signatureField.document_index) && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => goTo(signatureField.document_index, signatureField.page)}
                  >
                    Go to signature
                  </button>
                )}
              </span>
              <button
                className="btn btn-ghost"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}

          {documents.length > 1 && (
            <p className="small muted" style={{ marginTop: '.5rem' }}>
              Your signature goes on {documents.length} documents. Signing once signs them all,
              and each one is sealed over what you entered on it.
            </p>
          )}
        </div>

        <div className="stack">
          {invitation.party.length > 1 && <Party party={seen ? openedByYou(invitation.party) : invitation.party} />}

          {shownState === 'unreadable' ? (
            <CannotShow unshown={documents.length > 1 ? documents.filter((d) => viewing.cannot.includes(d.index)).map((d) => d.name) : undefined} />
          ) : !consented ? (
            <ConsentCard session={session} documentCount={documents.length} ready={shownState === 'shown'} onAgree={agree} error={error} />
          ) : (
            <div className="card">
              <h2>Your signature</h2>
              <p className="small muted">Draw your signature below using a mouse, trackpad or finger.</p>

              {fillable.length > 0 && (
                <div className="stack-tight" style={{ marginBottom: '1rem' }}>
                  {fillable.map((f) => (
                    <FieldInput
                      key={f.id}
                      field={f}
                      value={values[f.id] ?? ''}
                      onChange={(v) => setValues((prev) => ({ ...prev, [f.id]: v }))}
                      onGoToPage={() => goTo(f.document_index, f.page)}
                      onThisPage={f.page === page && f.document_index === docAt}
                      where={whereIs(f)}
                    />
                  ))}
                </div>
              )}

              <SignaturePad ref={pad} onChange={setHasInk} />

              <div className="row" style={{ marginBottom: '1rem' }}>
                <button className="btn btn-ghost" onClick={() => { pad.current?.clear(); setHasInk(false) }}>
                  Clear
                </button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="typed">Your full name</label>
                <input id="typed" type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)} />
                <p className="small faint" style={{ margin: '.25rem 0 0' }}>
                  If this differs from the name the sender used, the change is recorded in the audit
                  trail.
                </p>
              </div>

              {(unsignable || error) && (
                <div id="sign-error" className="notice notice-bad" role="alert" style={{ marginBottom: '1rem' }}>{unsignable || error}</div>
              )}

              {missing.length > 0 && (
                <p className="small faint" style={{ marginTop: 0, marginBottom: '.75rem' }}>
                  Still to fill in: {missing.map((f) => fieldTitle(f)).join(', ')}.
                </p>
              )}

              <button
                className="btn btn-lg"
                disabled={shownState !== 'shown' || unsignable !== '' || !hasInk || !typedName.trim() || missing.length > 0 || busy}
                onClick={submit}
              >
                {busy ? <><span className="spinner" /> Signing…</> : 'Sign document'}
              </button>

              <p className="small faint" style={{ marginTop: '.9rem', marginBottom: 0 }}>
                Signing seals the document cryptographically. Any later change to it becomes
                detectable by anyone who checks.
              </p>
            </div>
          )}

          <div className="card card-tight">
            {!declining ? (
              <button className="btn btn-ghost small" onClick={() => setDeclining(true)}>
                Decline to sign
              </button>
            ) : (
              <>
                <label htmlFor="reason">Why are you declining? (optional)</label>
                <textarea id="reason" rows={3} value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)} />
                {declineError && (
                  <div id="decline-error" className="notice notice-bad" role="alert" style={{ marginTop: '.75rem' }}>{declineError}</div>
                )}
                <div className="row" style={{ marginTop: '.75rem' }}>
                  <button className="btn btn-danger" onClick={decline} disabled={busy}>Confirm decline</button>
                  <button className="btn btn-ghost" onClick={() => setDeclining(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
